import { useRouter } from 'next/router';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import deserializeQueryStringItem from '@/helpers/deserializeQueryStringItem';
import isInIframe from '@/helpers/isInIframe';
import { type BeforeInstallPromptEvent, type InstallType, beforeinstallprompt, getAppInstallType } from '@/helpers/pwa';
import { logError } from '@/providers/ErrorTracking';
import { mixpanelTrack } from '@/providers/Mixpanel';

import DashboardAppContext from './Context';
import DashboardAppHead from './DashboardAppHead';
import getAppName from './getAppName';
import { type AppName } from './typings';

const SERVICE_WORKER_PATH = '/apps/dashboard/service-worker.js?v=1.0.1';
const SERVICE_WORKER_OPTIONS = {
  scope: '/dashboard',
};

type Props = {
  onInstalled?: () => unknown;
};

const DashboardAppProvider: React.FC<PropsWithChildren<Props>> = ({ children, onInstalled }) => {
  const router = useRouter();
  const [isReadyToInstall, setIsReadyToInstall] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [installType, setInstallType] = useState<InstallType>('unsupported');
  const [appName, setAppName] = useState<AppName>('web');

  const isInPWAQS = deserializeQueryStringItem(router.query['is-in-pwa'], { defaultValue: false, type: 'boolean' });

  const beforeInstallPromptEvent = useRef<BeforeInstallPromptEvent | null>(null);
  const onInstalledRef = useRef<null | (() => void)>(null);
  onInstalledRef.current = onInstalled || null;

  const isEnabled = installType !== 'unsupported' && !isInIframe();

  const doInstall = useCallback(async () => {
    if (beforeInstallPromptEvent.current?.prompt) {
      try {
        await beforeInstallPromptEvent.current.prompt();
        setIsInApp(true);
      } catch (error) {
        logError(error);
      }
    }
  }, []);

  useEffect(() => {
    setInstallType(getAppInstallType());
  }, []);

  useEffect(() => {
    const thisAppName = getAppName(router.pathname);
    if (
      isInPWAQS ||
      // iOS
      (window.navigator as unknown as { standalone: boolean }).standalone === true ||
      // desktop
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
    ) {
      setIsInApp(true);
      setAppName(thisAppName);
      try {
        sessionStorage.setItem('is-in-pwa', 'true');
        sessionStorage.setItem('pwa-app-name', thisAppName || '');
      } catch (_error) {
        /* */
      }
    } else {
      try {
        const isInPWAStored = sessionStorage.getItem('is-in-pwa') === 'true';
        const storedAppName = sessionStorage.getItem('pwa-app-name') as AppName;
        if (isInPWAStored) {
          setIsInApp(true);
          setAppName(storedAppName || thisAppName || 'other-app');
        } else {
          setIsInApp(false);
          setAppName('web');
        }
      } catch (_error) {
        setIsInApp(false);
        setAppName('web');
      }
    }
    // only want to fire onMount, not all route changes so don't add router.pathname to the dependency list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInPWAQS]);

  useEffect(() => {
    if (isEnabled && 'addEventListener' in window) {
      const handler = (): void => {
        mixpanelTrack('InstallNoodleApp - Install Completed');
        if (onInstalledRef.current) {
          onInstalledRef.current();
        }
      };
      window.addEventListener('appinstalled', handler);
      return () => window.removeEventListener('appinstalled', handler);
    }
    return () => undefined;
  }, [isEnabled]);

  useEffect(() => {
    if (isEnabled && 'addEventListener' in window) {
      const handler: EventListenerOrEventListenerObject = (event) => {
        event.preventDefault();
        beforeInstallPromptEvent.current = event as BeforeInstallPromptEvent;
        setIsReadyToInstall(true);
      };

      window.addEventListener(beforeinstallprompt, handler);
      return () => window.removeEventListener(beforeinstallprompt, handler);
    }
    return () => undefined;
  }, [isEnabled]);

  useEffect(() => {
    if (isEnabled && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register(SERVICE_WORKER_PATH, SERVICE_WORKER_OPTIONS)
        .then((registration) => {
          registration.update();

          // Listen for new service worker installation
          registration.addEventListener('updatefound', () => {
            // SW: Update found, installing new version
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // SW: New version installed, will activate on next page load
                  const shouldReload = confirm(
                    'There is a new version of the app available. Click here to reload the page and update.',
                  );
                  if (shouldReload) window.location.reload();
                }
              });
            }
          });
        })
        .catch((error) => {
          if (!error.message || !/The user denied permission to use Service Worker/.test(error.message)) {
            logError(error);
          }
        });

      return () => {
        navigator.serviceWorker.ready.then((registration) => registration.unregister());
      };
    }
    return () => undefined;
  }, [isEnabled]);

  const value = useMemo(
    () => ({
      appName,
      doInstall,
      installType,
      isInApp,
      isReadyToInstall,
    }),
    [appName, doInstall, isReadyToInstall, isInApp, installType],
  );

  return (
    <DashboardAppContext.Provider value={value}>
      <DashboardAppHead isEnabled={isEnabled} />
      {children}
    </DashboardAppContext.Provider>
  );
};

export default DashboardAppProvider;
